import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Divider,
  Dropdown,
  DropdownItem,
  DropdownList,
  MenuToggle,
  MenuToggleElement,
} from '@patternfly/react-core';
import { DotLoader } from 'react-spinners';
import { CheckCircleIcon, MinusCircleIcon } from '@patternfly/react-icons';
import useMobxStores from 'hooks/useMobxStores';
import { ILabVM, ILabVMAnnotation } from '../../types';
import './styles.scss';

type VMRowProps = {
  action: (command: string, params: { vm_id: string }) => void;
  labState?: string;
  vmAnnotations: ILabVMAnnotation[];
  vmSummary: ILabVM;
};

type VmDisplayStatus =
  | 'active'
  | 'stopped'
  | 'stopping'
  | 'starting'
  | 'transitional';

const normalizeState = (state: string | null | undefined) =>
  (state || '').toLowerCase().replace(/[\s-]+/g, '_');

const STOPPING_TASK_STATES = new Set([
  'powering_off',
  'shutting_down',
  'deleting',
]);

const STOPPING_VM_STATES = new Set(['stopping', 'shutting_down']);

const STARTING_TASK_STATES = new Set(['spawning', 'block_device_mapping']);

const STARTING_VM_STATES = new Set(['building', 'starting', 'spawning']);

const getVmDisplayStatus = (vm: ILabVM, labState?: string): VmDisplayStatus => {
  const vmState = normalizeState(vm.vm_state);
  const taskState = normalizeState(vm.task_state);

  if (
    STOPPING_VM_STATES.has(vmState) ||
    STOPPING_TASK_STATES.has(taskState) ||
    (labState === 'stopping' && vmState === 'active')
  ) {
    return 'stopping';
  }

  if (
    STARTING_VM_STATES.has(vmState) ||
    STARTING_TASK_STATES.has(taskState) ||
    (labState === 'starting' && vmState === 'stopped')
  ) {
    return 'starting';
  }

  if (vmState === 'stopped') {
    return 'stopped';
  }

  if (vmState === 'active') {
    return 'active';
  }

  return 'transitional';
};

const getVmStateLabel = (
  displayStatus: VmDisplayStatus,
  rawState: string,
  t: (key: string) => string,
): string => {
  switch (displayStatus) {
    case 'stopped':
      return t('Stopped');
    case 'active':
      return t('Active');
    case 'stopping':
      return t('Stopping');
    case 'starting':
      return t('Starting');
    default:
      return rawState;
  }
};

const isInhibit = (
  annotateName: string,
  annotations: ILabVMAnnotation[],
  vmName: string,
): boolean =>
  annotations.some(
    (a) => a.name === annotateName && a.vm_name === vmName && a.value === true,
  );

const VMRow: React.FC<VMRowProps> = observer(
  ({ action, labState, vmAnnotations, vmSummary: vm }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { t } = useTranslation();
    const { userStore } = useMobxStores();

    const { isInstructorOrAdmin } = userStore;
    const isInhibitConsole =
      isInhibit('inhibit_vnc_console', vmAnnotations, vm.name) &&
      !isInstructorOrAdmin;
    const isInhibitReset =
      !isInhibit('allow_reset', vmAnnotations, vm.name) && !isInstructorOrAdmin;

    const displayStatus = getVmDisplayStatus(vm, labState);
    const isLoading = ['stopping', 'starting', 'transitional'].includes(
      displayStatus,
    );
    const stateLabel = getVmStateLabel(displayStatus, vm.vm_state, t);
    const isVmActive = displayStatus === 'active';
    const isVmStopped = displayStatus === 'stopped';

    const handleClick = (command: string) => {
      action(command, { vm_id: vm.id });
    };

    const actionDropdown = (
      <Dropdown
        isOpen={isOpen}
        onOpenChange={(open) => setIsOpen(open)}
        onSelect={() => setIsOpen(false)}
        toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
          <MenuToggle
            id={`vm-action-dropdown-${vm.id}`}
            className="lab-card-vm-row__action-toggle"
            isExpanded={isOpen}
            onClick={() => setIsOpen((prev) => !prev)}
            ref={toggleRef}
            variant="default"
          >
            {t('Action')}
          </MenuToggle>
        )}
      >
        <DropdownList>
          <DropdownItem
            isDisabled={!isVmStopped}
            key="vm-action-start"
            onClick={() => handleClick('vm_start')}
          >
            {t('Start')}
          </DropdownItem>

          <Divider component="li" key="divider-1" />

          <DropdownItem
            isDisabled={!isVmActive}
            key="vm-action-shutdown"
            onClick={() => handleClick('vm_stop')}
          >
            {t('Shutdown')}
          </DropdownItem>

          <DropdownItem
            isDisabled={isVmStopped || isLoading}
            key="vm-action-poweroff"
            onClick={() => handleClick('vm_poweroff')}
          >
            {t('Power Off')}
          </DropdownItem>

          {!isInhibitReset && (
            <>
              <Divider component="li" key="divider-2" />

              <DropdownItem
                isDisabled={isVmStopped || isLoading}
                key="vm-action-reset"
                onClick={() => handleClick('vm_reset')}
              >
                {t('Reset')}
              </DropdownItem>
            </>
          )}
        </DropdownList>
      </Dropdown>
    );

    return (
      <div className="lab-card-vm-row" id={`vm-${vm.id}`}>
        <span className="lab-card-vm-row__name">{vm.name}</span>
        <div className="lab-card-vm-row__panel">
          <div className="lab-card-vm-row__actions">
            <span
              className={`lab-card__vm-status lab-card__vm-status--${displayStatus}`}
            >
              {displayStatus === 'active' ? (
                <CheckCircleIcon aria-hidden="true" />
              ) : null}
              {displayStatus === 'stopped' ? (
                <MinusCircleIcon aria-hidden="true" />
              ) : null}
              {stateLabel}
            </span>
            {isLoading ? (
              <span className="lab-controls-vm-panel-loading">
                <DotLoader color="#999" loading={isLoading} size={15} />
              </span>
            ) : null}
          </div>
          <div className="lab-card-vm-row__action">{actionDropdown}</div>
        </div>
        <div className="lab-card-vm-row__console">
          {!isInhibitConsole ? (
            <Button
              isDisabled={!isVmActive}
              onClick={() => handleClick('get_vnc_url')}
              variant="secondary"
            >
              {t('Open Console')}
            </Button>
          ) : null}
        </div>
      </div>
    );
  },
);

export default VMRow;
